/**
 * The Tools box's transforms (Synth VR): Note Rotation (SRVR-0352) and Scaling (SRVR-0353), and the rail
 * Rail Smoothing works on (SRVR-0354). Pure functions; the store resolves the range and writes the result.
 *
 * Both transforms pivot on the format's true centre, `GRID_CENTRE`, as Flip does. The desktop editor
 * pivoted on (0, 0) (`NoteRotationFunctions.GetRotatedPosition`, `Track.MoveSelectedNotes`), which is a
 * fraction of a cell off. Neither clamps to the grid, as in the desktop: the mapper chose its behaviour.
 */
import type { Note, Obstacle, Slide, ToolMode } from '../types/beatmap';
import { GRID_CENTRE } from '../components/three/placementGeometry';
import { noteFilterKind, type AreaObjects, type NoteFilterKind } from './areaSelection';
import type { RailPoint } from './railSmoothing';

const [CX, CY] = GRID_CENTRE;

/** A point turned `deg` degrees anticlockwise (positive Y up) about the true centre. */
export function rotateXY(x: number, y: number, deg: number): [number, number] {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const dx = x - CX;
  const dy = y - CY;
  return [CX + dx * c - dy * s, CY + dx * s + dy * c];
}

/** A point scaled by `sx` and `sy` (fractions, 1 = unchanged) about the true centre. */
export function scaleXY(x: number, y: number, sx: number, sy: number): [number, number] {
  return [CX + (x - CX) * sx, CY + (y - CY) * sy];
}

type XYMap = (x: number, y: number) => [number, number];

function mapNote(n: Note, f: XYMap): Note {
  const [x, y] = f(n.position[0], n.position[1]);
  const out: Note = { ...n, position: [x, y, n.position[2]] };
  if (n.rail_nodes) out.rail_nodes = n.rail_nodes.map(([nx, ny, nz]) => { const [a, b] = f(nx, ny); return [a, b, nz]; });
  return out;
}

export const rotateNote = (n: Note, deg: number): Note => mapNote(n, (x, y) => rotateXY(x, y, deg));
export const scaleNote = (n: Note, sx: number, sy: number): Note => mapNote(n, (x, y) => scaleXY(x, y, sx, sy));

/** A wall's file position scaled. A wall with no position is drawn at the centre, which scales onto
 *  itself, so it keeps none. Its own rotation is not touched (`Track.MoveSelectedWalls`). */
export function scaleWallPosition(pos: number[] | null | undefined, sx: number, sy: number): number[] | null | undefined {
  if (!pos || pos.length < 2) return pos;
  const [x, y] = scaleXY(pos[0], pos[1], sx, sy);
  return [x, y, ...pos.slice(2)];
}

/** Rotation turns the notes and rails only; walls are carried through unchanged. */
export function rotateObjects(objs: AreaObjects, deg: number): AreaObjects {
  return { ...objs, notes: objs.notes.map(n => rotateNote(n, deg)) };
}

/** Scaling moves notes, rail nodes and every positioned wall. `xPct`/`yPct` are percentages. */
export function scaleObjects(objs: AreaObjects, xPct: number, yPct: number): AreaObjects {
  const sx = xPct / 100;
  const sy = yPct / 100;
  const ob = <T extends Slide | Obstacle>(o: T): T => {
    const position = scaleWallPosition(o.position, sx, sy);
    return position === o.position ? o : { ...o, position };
  };
  return {
    notes: objs.notes.map(n => scaleNote(n, sx, sy)),
    slides: objs.slides.map(ob),
    crouches: objs.crouches.map(c => (c.obstacle ? { ...c, obstacle: ob(c.obstacle) } : c)),
    squares: objs.squares.map(ob),
    triangles: objs.triangles.map(ob),
  };
}

/** The note kind a placement tool puts down, for preferring a rail of the mapper's current hand. */
export function toolNoteKind(mode: ToolMode): NoteFilterKind | null {
  if (mode === 'note_right' || mode === 'rail_right') return 'right_hand';
  if (mode === 'note_left' || mode === 'rail_left') return 'left_hand';
  if (mode === 'note_onehand' || mode === 'rail_onehand') return 'one_hand_special';
  if (mode === 'note_bothhands' || mode === 'rail_bothhands') return 'both_hands_special';
  return null;
}

/**
 * `RailSmoothingFunctions.GetRailFromBeat`: the rail whose head is on `beat`. When it is the only note
 * there it is used whatever its kind; otherwise the first rail of the preferred kind, else the first
 * rail. A rail's head must be on the beat — a rail merely passing through it is not picked.
 */
export function pickRail(notes: readonly Note[], beat: number, tol: number, prefer: NoteFilterKind | null): number | null {
  const onBeat: number[] = [];
  notes.forEach((n, i) => { if (Math.abs(n.time_beats - beat) < tol) onBeat.push(i); });
  const isRail = (i: number) => !!notes[i].rail_nodes && notes[i].rail_nodes!.length > 0;
  const rails = onBeat.filter(isRail);
  if (!rails.length) return null;
  if (prefer) {
    const match = rails.find(i => noteFilterKind(notes[i]) === prefer);
    if (match !== undefined) return match;
  }
  return rails[0];
}

/** A rail as smoothing sees it: the head, then its nodes. */
export function railPoints(n: Note): RailPoint[] {
  return [[...n.position] as RailPoint, ...(n.rail_nodes ?? []).map(p => [...p] as RailPoint)];
}

/** A rail rebuilt from smoothed points; the head keeps its time, the nodes are the rest. */
export function withRailPoints(n: Note, pts: readonly RailPoint[]): Note {
  return {
    ...n,
    position: [pts[0][0], pts[0][1], n.position[2]],
    rail_nodes: pts.slice(1).map(p => [...p] as [number, number, number]),
    has_rail: true,
  };
}
